/** Client-side substring filtering for the Product Sync catalogue.
 *
 *  The whole catalogue is already in memory — the endpoint returns up to 5000
 *  rows in one response and caches them for an hour — so filtering here costs
 *  one pass over an array and avoids a round trip per keystroke.
 *
 *  Matching is plain case-insensitive substring, never a regex: an operator
 *  pasting a Salesforce id or a departure code must not have `.` or `+` treated
 *  as syntax, and a lone `(` must not throw.
 */
import type { ProductChild, ProductListItem } from '@/api/sync';

/** One run of text, flagged as matched or not. */
export interface HighlightSegment {
  text: string;
  match: boolean;
}

/** Split `text` on every occurrence of `query`, preserving the original casing.
 *
 *  Returns a single unmatched segment when there is nothing to highlight, so a
 *  caller can render the result unconditionally.
 */
export function highlightSegments(text: string, query: string): HighlightSegment[] {
  const needle = query.trim().toLowerCase();
  if (!needle || !text) return [{ text, match: false }];

  const haystack = text.toLowerCase();
  const out: HighlightSegment[] = [];
  let cursor = 0;
  for (;;) {
    const at = haystack.indexOf(needle, cursor);
    if (at === -1) break;
    if (at > cursor) out.push({ text: text.slice(cursor, at), match: false });
    out.push({ text: text.slice(at, at + needle.length), match: true });
    cursor = at + needle.length;
  }
  if (cursor === 0) return [{ text, match: false }];
  if (cursor < text.length) out.push({ text: text.slice(cursor), match: false });
  return out;
}

function hit(value: string | null | undefined, needle: string): boolean {
  return Boolean(value) && String(value).toLowerCase().includes(needle);
}

/** Everything about a row an operator might reasonably type a fragment of. */
function childMatches(child: ProductChild, needle: string): boolean {
  return (
    hit(child.id, needle) ||
    hit(child.name, needle) ||
    hit(child.kind, needle) ||
    (child.facets ?? []).some((f) => hit(f.value, needle) || hit(f.label, needle))
  );
}

function productMatches(product: ProductListItem, needle: string): boolean {
  return (
    hit(product.id, needle) ||
    hit(product.name, needle) ||
    (product.facets ?? []).some((f) => hit(f.value, needle) || hit(f.label, needle))
  );
}

/** A product that survived the filter, and which of its children to show. */
export interface FilteredProduct {
  product: ProductListItem;
  /** The children to render. Every child when the product itself matched or the
   *  filter is empty; only the matching ones when the product was kept *because*
   *  a child matched. */
  children: ProductChild[];
  /** True when the product was kept only because a child matched — the row is
   *  then expanded on sight, because a collapsed row would give the operator a
   *  result with no visible reason for being there. */
  matchedViaChild: boolean;
}

/**
 * Keep the products matching `query`, and decide which children each shows.
 *
 * An empty query keeps everything untouched, so the unfiltered page pays only
 * for the map.
 */
export function filterProducts(products: ProductListItem[], query: string): FilteredProduct[] {
  const needle = query.trim().toLowerCase();
  if (!needle) {
    return products.map((product) => ({
      product,
      children: product.children,
      matchedViaChild: false,
    }));
  }

  const out: FilteredProduct[] = [];
  for (const product of products) {
    if (productMatches(product, needle)) {
      // The product itself matched, so every departure under it is a legitimate
      // result — narrowing them here would hide siblings the operator can see
      // the moment they clear the box.
      out.push({ product, children: product.children, matchedViaChild: false });
      continue;
    }
    const children = product.children.filter((c) => childMatches(c, needle));
    if (children.length) out.push({ product, children, matchedViaChild: true });
  }
  return out;
}
